/**
 * The app's editor hints (OnboardingFlowController.ShowNextEditorHints and its balloons), one at a time and each once
 * in this browser (`src/state/editorHints.ts`): a balloon beside what it is about, a ring round it, the app's words and
 * «Понятно». It comes a moment after its step is in reach, as the app waits before it, and goes once that step is
 * taken, after its time where the app gives it one, with Escape, or when a panel or a gesture takes the screen.
 *
 * - «Нажми здесь, чтобы начать рисовать стены» (DrawWallsBalloonHint): a new project's empty floor plan, at «Стены»; 10 s.
 * - «Передвинь стену, чтобы изменить размер комнаты» (CanMoveWall → HighlightPlanWallFlowItem): its first room on the
 *   plan, on the room's longest wall where a press moves it (`planWall`); 10 s.
 * - «Перейди в 3D, чтобы добавить мебель и шторы» on the plan, «Нажми здесь, чтобы задать форму и размер комнаты» in 3D
 *   (EditRoomBlueCircleHintFlowItem): the room of a new project, at the other half of the 2D/3D switch.
 * - «Круто! В твоей комнате появилась мебель…» (EditMakePhotoBlueCircleHintFlowItem): in 3D once the floor holds
 *   PHOTO_HINT_ITEMS pieces, PHOTO_HINT_ADDED of them added this visit, at «Сделать фото», with «Сделать фото».
 * - «Нажми на кнопку, чтобы клонировать мебель» (ShowCloneHint): a nightstand selected, at its «Дублировать»; 6 s.
 */
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { Level, Vec2 } from '../core/hp/types';
import { projectPoint } from '../core/geometry/edit';
import type { HitTarget, PlanView } from '../render/plan/PlanView';
import { hintShown, isNewProject, markHintShown, PHOTO_HINT_ADDED, PHOTO_HINT_ITEMS, type HintId } from '../state/editorHints';
import { store, type EditorState } from '../state/store';
import { usePresence } from './usePresence';
import { useStore } from './useStore';
import { t as tr } from '../i18n/I18n';

type Side = 'left' | 'below' | 'above';
type Target = { rect: DOMRect } | { point: { x: number; y: number } };
interface Hint {
  id: HintId;
  title?: string;
  text: string;
  /** What it points at on screen: a control, or a point of the drawing. */
  target: () => Target | null;
  side: Side;
  /** Seconds before it shows once its step is in reach (the app's WaitShort, WaitMedium). */
  delay: number;
  /** Seconds before it goes by itself, as the app's CloseTimeoutSeconds; without one it waits for «Понятно» or its step. */
  timeout?: number;
  action?: { label: string; run: () => void };
}

const MARGIN = 12, GAP = 14;
const rectOf = (selector: string): Target | null => {
  const element = document.querySelector<HTMLElement>(selector);
  if (!element || !element.offsetParent) return null;
  return { rect: element.getBoundingClientRect() };
};
/** The tab of the 2D/3D switch with this label. */
const tab = (label: '2D' | '3D'): Target | null => {
  const button = [...document.querySelectorAll<HTMLButtonElement>('[role=tab]')].find(element => element.textContent?.trim() === label && element.offsetParent);
  return button ? { rect: button.getBoundingClientRect() } : null;
};
/** Along a side, the places tried for the ring: clear of the middle and of the corners. */
const WALL_SPOTS = [.3, .7, .25, .75, .35, .65, .2, .8, .4, .6, .15, .85];
let wallSpot: { level: Level; view: string; selection: unknown; point: Vec2 | null } | null = null;
/**
 * Where the first room's wall is pressed to be moved: on its longest side if it can be, as the app highlights that wall.
 * Not at the middle, where the app's move handle is: there the plan keeps its corner-to-be point, which splits the wall,
 * and a door or a piece against the wall takes a press before the wall does. The plan's own hit test decides, for a
 * mouse and for a finger alike; with no such place in view there is no ring.
 */
function planWall(level: Level | null | undefined): Target | null {
  const canvas = document.querySelector<HTMLCanvasElement>('canvas.plan-canvas'), room = level?.rooms[0];
  const view = (canvas as unknown as { __planView?: PlanView } | null)?.__planView;
  if (!canvas || !view || !level || !room || room.polygon.length < 3) return null;
  const origin = view.toScreen({ x: 0, z: 0 }), unit = view.toScreen({ x: 1, z: 0 });
  const viewKey = `${origin.x},${origin.y},${unit.x},${unit.y},${canvas.clientWidth},${canvas.clientHeight}`, selection = store.get().selection;
  if (!wallSpot || wallSpot.level !== level || wallSpot.view !== viewKey || wallSpot.selection !== selection) {
    const sides = room.polygon.map((a, i) => [a, room.polygon[(i + 1) % room.polygon.length]] as const)
      .sort(([a, b], [c, d]) => Math.hypot(d.x - c.x, d.z - c.z) - Math.hypot(b.x - a.x, b.z - a.z));
    const onSide = (target: HitTarget, p: Vec2) => {
      const wall = target?.kind === 'wall' ? level.walls.find(value => value.id === target.id) : undefined;
      return !!wall && projectPoint(p, wall.start, wall.end).distance < wall.thickness;
    };
    let point: Vec2 | null = null;
    for (const [a, b] of sides) {
      for (const t of WALL_SPOTS) {
        const p = { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t }, s = view.toScreen(p);
        if (s.x <= 0 || s.y <= 0 || s.x >= canvas.clientWidth || s.y >= canvas.clientHeight) continue;
        if (onSide(view.hit(s.x, s.y, level, selection), p) && onSide(view.hit(s.x, s.y, level, selection, 'floor', false, true), p)) { point = p; break; }
      }
      if (point) break;
    }
    wallSpot = { level, view: viewKey, selection, point };
  }
  if (!wallSpot.point) return null;
  const rect = canvas.getBoundingClientRect(), p = view.toScreen(wallSpot.point);
  return { point: { x: rect.left + p.x, y: rect.top + p.y } };
}

const select = (s: EditorState) => {
  const level = s.scene?.levels[s.activeLevelIndex];
  const item = s.selection?.kind === 'item' ? level?.items.find(value => value.id === (s.selection as { id: string }).id) : undefined;
  return { mode: s.mode, planSurface: s.planSurface, tool: s.tool, projectId: s.projectId, loading: s.loading, isDragging: s.isDragging, edit: s.edit,
    firstPerson: s.firstPerson, cameraMode: s.cameraMode, walls: level?.walls.length ?? 0, rooms: level?.rooms.length ?? 0, items: level?.items.length ?? 0,
    wallsKey: level ? level.walls.map(wall => `${wall.start.x},${wall.start.z},${wall.end.x},${wall.end.z}`).join(';') : '',
    selected: s.selection?.kind === 'item' ? s.selection.id : null, nightstand: !!item?.tags?.includes('Nightstand'), level: s.activeLevelIndex };
};

export function EditorHints({ blocked }: { blocked: boolean }) {
  const state = useStore(select);
  // Pieces added while this project is open: the photo hint waits for the room to be furnished this visit.
  const added = useRef({ key: '', last: 0, count: 0 });
  const scope = `${state.projectId}:${state.level}`;
  if (added.current.key !== scope) added.current = { key: scope, last: state.items, count: 0 };
  else if (state.items !== added.current.last) {
    if (state.items > added.current.last) added.current.count += state.items - added.current.last;
    added.current.last = state.items;
  }

  const fresh = isNewProject(state.projectId), plan = state.mode === 'plan' && state.planSurface === 'floor', scene = state.mode === 'scene';
  // In a shared project walls and rooms are the app's: no hint offers to draw, move or shape them.
  const builds = state.edit === 'all';
  const quiet = blocked || state.loading || state.isDragging || !state.projectId;
  /** The hint whose step is in reach now, the app's order: drawing first, then the room, then the furnishing. */
  const due: Hint | null = quiet ? null
    : builds && fresh && plan && state.walls === 0 && state.tool === 'navigate' && !hintShown('drawWalls')
      ? { id: 'drawWalls', text: tr('#DrawWallHint'), side: 'left', delay: 1, timeout: 10, target: () => rectOf(`.rail-build button[aria-label="${tr('#EditorDrawWalls')}"]`) }
    : builds && fresh && plan && state.rooms > 0 && state.tool === 'navigate' && !hintShown('moveWall')
      ? { id: 'moveWall', text: tr('#MoveWallHint'), side: 'above', delay: 1.5, timeout: 10, target: () => planWall(store.level) }
    : fresh && state.rooms > 0 && (plan || builds && scene && !state.firstPerson) && state.tool === 'navigate' && !hintShown('room2d3d')
      ? plan ? { id: 'room2d3d', text: tr('#Furnish3DHintTitle'), side: 'below', delay: 2, target: () => tab('3D') }
        : { id: 'room2d3d', text: tr('#EditRoomHintTitle'), side: 'below', delay: 2, target: () => tab('2D') }
    : scene && !state.cameraMode && state.items >= PHOTO_HINT_ITEMS && added.current.count >= PHOTO_HINT_ADDED && !hintShown('makePhoto')
      ? { id: 'makePhoto', title: tr('#Awesome'), text: tr('#EditorHintMakePhoto'), side: 'below', delay: 2,
        target: () => rectOf('.top-photo'), action: { label: tr('#MakePhoto'), run: () => document.querySelector<HTMLButtonElement>('.top-photo')?.click() } }
    : state.nightstand && !hintShown('cloneNightstand')
      ? { id: 'cloneNightstand', text: tr('#CloneNightstandHint'), side: 'below', delay: .6, timeout: 6, target: () => rectOf(`.context-bar button[aria-label="${tr('#ToolbarDuplicateItem')}"]`) }
    : null;

  const [shown, setShown] = useState<{ hint: Hint; at: string } | null>(null);
  // What the screen was when the hint came: the step it stands for is taken once this changes.
  const moment = `${state.mode}:${state.planSurface}:${state.tool}:${state.wallsKey}:${state.selected}:${state.items}:${state.cameraMode}`;
  const momentRef = useRef(moment); momentRef.current = moment;
  const dueId = due?.id ?? null, dueRef = useRef(due); dueRef.current = due;
  useEffect(() => {
    if (!dueId || shown) return;
    const timer = setTimeout(() => {
      const hint = dueRef.current;
      if (!hint || hint.id !== dueId || !hint.target()) return;
      markHintShown(hint.id);
      setShown({ hint, at: momentRef.current });
    }, (dueRef.current?.delay ?? 1) * 1000);
    return () => clearTimeout(timer);
  }, [dueId, shown]);
  // Its step taken, a panel or a gesture in its way: it goes.
  useEffect(() => { if (shown && (quiet || moment !== shown.at)) setShown(null); }, [shown, quiet, moment]);
  useEffect(() => {
    if (!shown?.hint.timeout) return;
    const timer = setTimeout(() => setShown(null), shown.hint.timeout * 1000);
    return () => clearTimeout(timer);
  }, [shown]);
  // Escape puts it away; so does a press anywhere but on it, which goes on to what it pressed, as the app's hints go
  // at a touch — it is a word on the way, not a step to take.
  useEffect(() => {
    if (!shown) return;
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setShown(null); } };
    const press = (event: PointerEvent) => { if (!(event.target instanceof Element && event.target.closest('.editor-hint'))) setShown(null); };
    document.addEventListener('keydown', key, true);
    document.addEventListener('pointerdown', press, true);
    return () => { document.removeEventListener('keydown', key, true); document.removeEventListener('pointerdown', press, true); };
  }, [shown]);

  const presence = usePresence(!!shown, 140);
  const last = useRef<Hint | null>(null);
  if (shown) last.current = shown.hint;
  const hint = last.current;
  if (!presence.mounted || !hint) return null;
  return createPortal(<HintBalloon hint={hint} closing={presence.closing} onClose={() => setShown(null)} />, document.body);
}

/** The balloon and its ring, following what they point at every frame (a control that moves, a plan that pans). */
function HintBalloon({ hint, closing, onClose }: { hint: Hint; closing: boolean; onClose: () => void }) {
  const balloon = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ ring: { x: number; y: number; width: number; height: number; round: boolean }; x: number; y: number; side: Side; arrow: number } | null>(null);
  const lost = useRef(onClose); lost.current = onClose;
  useLayoutEffect(() => {
    let frame = 0, previous = '';
    const follow = () => {
      frame = requestAnimationFrame(follow);
      const target = hint.target(), box = balloon.current?.getBoundingClientRect();
      // Gone from the screen, or a menu or a list opened over it: the hint goes.
      if (!target || document.querySelector('[role=menu], [role=listbox], dialog[open]')) { if (!closing) lost.current(); return; }
      if (!box) return;
      const ring = 'rect' in target ? { x: target.rect.left - 5, y: target.rect.top - 5, width: target.rect.width + 10, height: target.rect.height + 10, round: false }
        : { x: target.point.x - 24, y: target.point.y - 24, width: 48, height: 48, round: true };
      const cx = ring.x + ring.width / 2, cy = ring.y + ring.height / 2, w = box.width, h = box.height;
      let side = hint.side;
      // A side with no room for the balloon gives way to the other one.
      if (side === 'left' && ring.x - GAP - w < MARGIN) side = 'below';
      if (side === 'below' && ring.y + ring.height + GAP + h > innerHeight - MARGIN) side = 'above';
      if (side === 'above' && ring.y - GAP - h < MARGIN) side = 'below';
      const x = side === 'left' ? ring.x - GAP - w : Math.min(Math.max(cx - w / 2, MARGIN), innerWidth - w - MARGIN);
      const y = side === 'left' ? Math.min(Math.max(cy - h / 2, MARGIN), innerHeight - h - MARGIN) : side === 'below' ? ring.y + ring.height + GAP : ring.y - GAP - h;
      const arrow = side === 'left' ? Math.min(Math.max(cy - y, 18), h - 18) : Math.min(Math.max(cx - x, 18), w - 18);
      const next = JSON.stringify([ring, Math.round(x), Math.round(y), side, Math.round(arrow)]);
      if (next !== previous) { previous = next; setPlace({ ring, x: Math.round(x), y: Math.round(y), side, arrow: Math.round(arrow) }); }
    };
    follow();
    return () => cancelAnimationFrame(frame);
  }, [hint, closing]);
  return <>
    {place && <span className={'editor-hint-ring' + (place.ring.round ? ' is-round' : '') + (closing ? ' is-leaving' : '')} aria-hidden="true"
      style={{ left: place.ring.x, top: place.ring.y, width: place.ring.width, height: place.ring.height }} />}
    <div ref={balloon} className={'editor-hint' + (closing ? ' is-leaving' : '')} role="status" data-hint={hint.id} data-side={place?.side ?? hint.side}
      style={place ? { left: place.x, top: place.y, '--hint-arrow': `${place.arrow}px` } as CSSProperties : { left: -9999, top: -9999 }}>
      <p>{hint.title && <strong>{hint.title} </strong>}{hint.text}</p>
      <div className="editor-hint-actions">
        {hint.action && <button type="button" className="text-button editor-hint-action" onClick={() => { const run = hint.action!.run; onClose(); run(); }}>{hint.action.label}</button>}
        <button type="button" className="text-button" onClick={onClose}>{tr('#GotIt')}</button>
      </div>
    </div>
  </>;
}
